import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, HelpCircle, Inbox, Plus, Paperclip, X, Send, Clock, CheckCircle2, MessageSquare } from "lucide-react";

const STATUS_META = {
  open: { label: "Open", color: "#60a5fa" },
  in_progress: { label: "In progress", color: "#f5a623" },
  resolved: { label: "Resolved", color: "#22c55e" },
  closed: { label: "Closed", color: "#888888" },
};

const MAX_FILES = 3;
const MAX_BYTES = 5 * 1024 * 1024; // 5MB

export default function Support() {
  const [tab, setTab] = useState("new");
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openTicket, setOpenTicket] = useState(null);

  const loadTickets = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/support/tickets");
      setTickets(data || []);
    } catch (err) {
      toast.error("Could not load tickets");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line
  // eslint-disable-next-line
  useEffect(() => {
    if (tab === "tickets") loadTickets();
  }, [tab]);

  return (
    <div className="max-w-3xl mx-auto" data-testid="support-page">
      <Link to="/dashboard" className="flex items-center gap-1.5 mb-5 text-sm transition hover:opacity-80" style={{ color: "var(--text-muted)" }} data-testid="support-back-link">
        <ArrowLeft size={14} /> Back
      </Link>

      <div className="flex items-center gap-3 mb-2">
        <div className="flex items-center justify-center rounded-2xl" style={{ width: 48, height: 48, background: "var(--lime-glow)", color: "var(--lime)", border: "1px solid var(--border-lime)" }}>
          <HelpCircle size={24} />
        </div>
        <div>
          <h1 className="h-display text-3xl sm:text-4xl">Support</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>Get help and find answers to your questions.</p>
        </div>
      </div>

      {/* Tab switcher */}
      <div
        className="grid grid-cols-2 gap-1 p-1 rounded-xl mt-6 mb-6"
        style={{ background: "var(--surface2)", border: "1px solid var(--border)" }}
      >
        <button
          onClick={() => { setTab("new"); setOpenTicket(null); }}
          data-testid="support-tab-new"
          className="flex items-center justify-center gap-2 py-2.5 rounded-2xl text-sm transition"
          style={{
            background: tab === "new" ? "var(--surface)" : "transparent",
            color: tab === "new" ? "var(--lime)" : "var(--text-muted)",
            border: tab === "new" ? "1px solid var(--border-lime)" : "1px solid transparent",
          }}
        >
          <Plus size={14} /> New
        </button>
        <button
          onClick={() => { setTab("tickets"); setOpenTicket(null); }}
          data-testid="support-tab-tickets"
          className="flex items-center justify-center gap-2 py-2.5 rounded-2xl text-sm transition"
          style={{
            background: tab === "tickets" ? "var(--surface)" : "transparent",
            color: tab === "tickets" ? "var(--lime)" : "var(--text-muted)",
            border: tab === "tickets" ? "1px solid var(--border-lime)" : "1px solid transparent",
          }}
        >
          <Inbox size={14} /> Tickets {tickets.length > 0 && <span className="font-mono text-[10px]" style={{ color: "var(--text-muted)" }}>{tickets.length}</span>}
        </button>
      </div>

      {tab === "new" && <NewTicketForm onCreated={() => { setTab("tickets"); }} />}
      {tab === "tickets" && !openTicket && (
        <TicketList
          tickets={tickets}
          loading={loading}
          onOpen={(t) => setOpenTicket(t)}
          onEmpty={() => setTab("new")}
        />
      )}
      {tab === "tickets" && openTicket && (
        <TicketDetail
          ticketId={openTicket.id}
          onBack={() => setOpenTicket(null)}
          onChange={loadTickets}
        />
      )}
    </div>
  );
}

function NewTicketForm({ onCreated }) {
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [attachments, setAttachments] = useState([]); // [{file_id, filename}]
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleFiles = async (files) => {
    const arr = Array.from(files || []);
    if (!arr.length) return;
    if (attachments.length + arr.length > MAX_FILES) {
      toast.error(`Max ${MAX_FILES} files total`);
      return;
    }
    setUploading(true);
    try {
      for (const f of arr) {
        if (f.size > MAX_BYTES) {
          toast.error(`${f.name} is too large (max 5MB)`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setAttachments((prev) => [...prev, { file_id: data.id, filename: f.name }]);
      }
    } catch (err) {
      toast.error("Upload failed", { description: err?.response?.data?.detail || err.message });
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = (idx) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!subject.trim()) return toast.error("Subject is required");
    if (!description.trim()) return toast.error("Description is required");
    setBusy(true);
    try {
      await api.post("/support/tickets", {
        subject: subject.trim(),
        description: description.trim(),
        priority: "medium",
        attachments,
      });
      toast.success("Ticket submitted. We'll get back to you soon.");
      setSubject(""); setDescription(""); setAttachments([]);
      onCreated?.();
    } catch (err) {
      toast.error("Could not submit ticket", { description: err?.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="surface-card p-6" data-testid="support-new-form">
      <h2 className="h-display text-2xl mb-6">Create New Ticket</h2>

      <div className="mb-4">
        <label className="field-label">Subject <span className="req">*</span></label>
        <input
          className="input-base"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Brief description of your issue"
          data-testid="support-subject-input"
        />
      </div>

      <div className="mb-4">
        <label className="field-label">Description <span className="req">*</span></label>
        <textarea
          rows={6}
          className="input-base"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Please provide as much detail as possible about your issue..."
          data-testid="support-description-input"
        />
      </div>

      <div className="mb-6">
        <label className="field-label">Attachments</label>
        <label
          className="btn-ghost text-sm inline-flex items-center gap-2 cursor-pointer"
          data-testid="support-attach-trigger"
        >
          <Paperclip size={14} /> {uploading ? "Uploading..." : "Attach files"}
          <input
            type="file"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
            disabled={uploading || attachments.length >= MAX_FILES}
            data-testid="support-file-input"
          />
        </label>
        <p className="text-[11px] mt-1" style={{ color: "var(--text-dim)" }}>
          Max {MAX_FILES} files, 5MB each. Images, PDFs, or documents.
        </p>
        {attachments.length > 0 && (
          <div className="mt-2 space-y-1.5">
            {attachments.map((a, i) => (
              <div
                key={a.file_id}
                className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs"
                style={{ background: "var(--surface2)", border: "1px solid var(--border)" }}
                data-testid={`support-attachment-${i}`}
              >
                <Paperclip size={12} style={{ color: "var(--text-muted)" }} />
                <span className="flex-1 truncate">{a.filename}</span>
                <button
                  type="button"
                  onClick={() => removeAttachment(i)}
                  className="p-0.5 rounded transition hover:opacity-70"
                  style={{ color: "var(--red)" }}
                  aria-label="Remove"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <button
        type="submit"
        disabled={busy || !subject.trim() || !description.trim()}
        className="btn-primary w-full flex items-center justify-center gap-2 text-base py-3"
        data-testid="support-submit-button"
      >
        {busy ? <span className="spinner" /> : <Send size={14} />}
        {busy ? "Submitting..." : "Submit Ticket"}
      </button>
    </form>
  );
}

function TicketList({ tickets, loading, onOpen, onEmpty }) {
  if (loading) return <div className="surface-card p-6 text-center text-sm" style={{ color: "var(--text-muted)" }}>Loading tickets…</div>;
  if (tickets.length === 0) {
    return (
      <div className="surface-card p-10 text-center" data-testid="support-tickets-empty">
        <Inbox size={32} style={{ color: "var(--text-muted)" }} className="mx-auto mb-3" />
        <h3 className="font-semibold mb-1">No tickets yet</h3>
        <p className="text-sm mb-4" style={{ color: "var(--text-muted)" }}>When you submit a support request, it&apos;ll show up here.</p>
        <button onClick={onEmpty} className="btn-primary text-sm inline-flex items-center gap-2">
          <Plus size={14} /> Create your first ticket
        </button>
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="support-tickets-list">
      {tickets.map((t) => {
        const meta = STATUS_META[t.status] || STATUS_META.open;
        return (
          <button
            key={t.id}
            onClick={() => onOpen(t)}
            className="w-full text-left p-4 rounded-xl flex items-start gap-3 transition hover:opacity-90"
            style={{ background: "var(--surface)", border: "1px solid var(--border)" }}
            data-testid={`support-ticket-${t.id}`}
          >
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm truncate">{t.subject}</div>
              <div className="flex items-center gap-3 mt-1 flex-wrap text-[11px]" style={{ color: "var(--text-muted)" }}>
                <span className="font-mono uppercase tracking-wider" style={{ color: meta.color }}>{meta.label}</span>
                <span className="font-mono uppercase tracking-wider">{t.priority}</span>
                <span className="flex items-center gap-1"><Clock size={10} /> {new Date(t.updated_at).toLocaleDateString()}</span>
                {t.messages?.length > 0 && (
                  <span className="flex items-center gap-1"><MessageSquare size={10} /> {t.messages.length}</span>
                )}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function TicketDetail({ ticketId, onBack, onChange }) {
  const [ticket, setTicket] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/support/tickets/${ticketId}`);
      setTicket(data);
    } catch (err) {
      toast.error("Could not load ticket");
    }
  };

  // eslint-disable-next-line
  useEffect(() => { load(); }, [ticketId]);

  const sendReply = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/support/tickets/${ticketId}/reply`, { body: reply.trim() });
      setTicket(data);
      setReply("");
      onChange?.();
    } catch (err) {
      toast.error("Could not send reply");
    } finally {
      setBusy(false);
    }
  };

  if (!ticket) return <div className="surface-card p-6 text-center text-sm" style={{ color: "var(--text-muted)" }}>Loading…</div>;

  const meta = STATUS_META[ticket.status] || STATUS_META.open;
  return (
    <div data-testid="support-ticket-detail">
      <button onClick={onBack} className="flex items-center gap-1.5 mb-3 text-sm transition hover:opacity-80" style={{ color: "var(--text-muted)" }}>
        <ArrowLeft size={14} /> All tickets
      </button>
      <div className="surface-card p-6 mb-4">
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <span className="font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded" style={{ background: `${meta.color}22`, color: meta.color }}>{meta.label}</span>
          <span className="font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded" style={{ background: "var(--surface2)", color: "var(--text-muted)" }}>{ticket.priority}</span>
        </div>
        <h2 className="h-display text-xl mb-3">{ticket.subject}</h2>
        <p className="text-sm whitespace-pre-wrap" style={{ color: "var(--text)" }}>{ticket.description}</p>
        {ticket.attachments?.length > 0 && (
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {ticket.attachments.map((a) => (
              <span key={a.file_id} className="px-3 py-1.5 rounded-xl text-xs flex items-center gap-1.5" style={{ background: "var(--surface2)", border: "1px solid var(--border)" }}>
                <Paperclip size={11} /> {a.filename}
              </span>
            ))}
          </div>
        )}
      </div>

      {ticket.messages?.length > 0 && (
        <div className="space-y-3 mb-4">
          {ticket.messages.map((m) => (
            <div
              key={m.id}
              className="surface-card p-4"
              style={{
                }}
            >
              <div className="flex items-center gap-2 mb-2 text-[11px]" style={{ color: "var(--text-muted)" }}>
                <span className="font-semibold" style={{ color: m.author_is_admin ? "var(--lime)" : "var(--text)" }}>
                  {m.author_is_admin ? "Coach" : (m.author_name || "You")}
                </span>
                <span className="font-mono">{new Date(m.created_at).toLocaleString()}</span>
              </div>
              <p className="text-sm whitespace-pre-wrap">{m.body}</p>
            </div>
          ))}
        </div>
      )}

      {ticket.status !== "closed" && (
        <div className="surface-card p-4">
          <textarea
            rows={3}
            className="input-base"
            placeholder="Add a reply or follow-up question…"
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            data-testid="support-reply-input"
          />
          <div className="mt-3 flex justify-end">
            <button onClick={sendReply} disabled={busy || !reply.trim()} className="btn-primary text-sm flex items-center gap-2" data-testid="support-reply-button">
              {busy ? <span className="spinner" /> : <Send size={13} />} Send reply
            </button>
          </div>
        </div>
      )}

      {ticket.status === "resolved" && (
        <div className="mt-3 p-3 rounded-2xl flex items-center gap-2 text-xs" style={{ background: "var(--surface2)", color: "var(--success)" }}>
          <CheckCircle2 size={14} /> This ticket was marked as resolved.
        </div>
      )}
    </div>
  );
}
